import { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { compressImage } from '../lib/compressImage';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function ImageUploadField({ value, onChange, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      if (compressed.size > 10 * 1024 * 1024) {
        toast.error('Image must be smaller than 10MB');
        return;
      }
      const form = new FormData();
      form.append('file', compressed, compressed === file ? file.name : 'photo.jpg');
      const res = await axios.post(`${API}/upload-image`, form, { headers: { Authorization: `Bearer ${token}` } });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="image-upload-field">
      <label className="block text-sm font-medium text-espresso mb-1">Image</label>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-16 h-16 flex-shrink-0">
            <img src={value} alt="Preview" className="w-16 h-16 rounded-lg object-cover border" data-testid="image-upload-preview" />
            <button type="button" onClick={() => onChange('')} className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5" data-testid="image-upload-remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : null}
        <Button type="button" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} className="gap-2" data-testid="image-upload-button">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="image-upload-input" />
      </div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="...or paste an image link (https://...)" data-testid="image-url-input" />
    </div>
  );
}
